import React from "react";
import { Link } from "react-router-dom";
import { 
  Check, ArrowRight, ArrowLeft, TrendingUp, Target, Layout, 
  Cog, BarChart3, Zap, Rocket
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead from "../components/SEOHead";

const PackCroissancePage = () => {
  const { t, language } = useLanguage();

  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: t('packs.growthFeature1Title'),
      desc: t('packs.growthFeature1Desc')
    },
    {
      icon: <Layout className="w-6 h-6" />,
      title: t('packs.growthFeature2Title'),
      desc: t('packs.growthFeature2Desc')
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: t('packs.growthFeature3Title'),
      desc: t('packs.growthFeature3Desc')
    },
    {
      icon: <Cog className="w-6 h-6" />,
      title: t('packs.growthFeature4Title'),
      desc: t('packs.growthFeature4Desc')
    }
  ];

  const benefits = [
    t('packs.growthBenefit1'),
    t('packs.growthBenefit2'),
    t('packs.growthBenefit3'),
    t('packs.growthBenefit4')
  ];

  return (
    <div className="pt-20">
      <SEOHead 
        title={language === 'fr' 
          ? "Pack Croissance + | ImpaKt Studio - Acquisition et Leads Qualifiés" 
          : "Growth + Pack | ImpaKt Studio - Acquisition and Qualified Leads"
        }
        description={language === 'fr'
          ? "Pack Croissance + à partir de 3 000€. Tunnel de conversion, campagnes ads, automation email. Système d'acquisition performant."
          : "Growth + Pack starting at €3,000. Conversion funnel, ad campaigns, email automation. High-performance acquisition system."
        }
        path="/offres/croissance-plus"
      />
      
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 to-teal-500 overflow-hidden" data-testid="pack-croissance-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/offres" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
            data-testid="back-to-offres"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('common.backToOffers')}
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4" />
                {t('packs.strategicPack')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {t('packs.growthName')}
              </h1>
              <p className="text-xl text-white/90 mb-4 font-medium">
                {t('packs.growthTagline')}
              </p>
              <p className="text-lg text-white/80 mb-8">
                {t('packs.growthDesc')}
              </p>
              
              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-white/70 text-lg">{t('common.startingFrom')}</span>
                <span className="text-5xl font-bold text-white">3 000 €</span>
              </div>
              
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-xl"
                data-testid="cta-croissance"
              >
                {t('packs.growthCta')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80"
                alt={language === 'fr' ? "Croissance et performance" : "Growth and performance"}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-900">{t('packs.qualifiedLeads')}</div>
                    <div className="text-gray-600 text-sm">{t('packs.continuously')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ce qui est inclus */}
      <section className="py-20 bg-white" data-testid="pack-croissance-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">{t('packs.whatsIncluded')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {t('packs.completeAcquisition')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Résultat */}
      <section className="py-20 bg-emerald-50" data-testid="pack-croissance-result">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                alt={language === 'fr' ? "Dashboard de performance" : "Performance dashboard"}
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">{t('packs.theResult')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
                {t('packs.growthResultTitle')}
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                {t('packs.growthResultDesc')}
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Note importante */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-amber-800 mb-2">{t('packs.importantNote')}</h3>
            <p className="text-amber-700">
              {t('packs.growthNote')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-500" data-testid="pack-croissance-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('packs.growthCtaTitle')}
          </h2>
          <p className="text-white/80 text-lg mb-4">
            {t('packs.growthName')} {t('common.startingFrom').toLowerCase()} <strong className="text-white">3 000 €</strong>
          </p>
          <p className="text-white/70 mb-10">
            {language === 'fr' ? "Contactez-nous pour définir ensemble votre stratégie d'acquisition" : "Contact us to define your acquisition strategy together"}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-emerald-600 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg"
              data-testid="cta-contact-croissance"
            >
              {t('packs.growthCta')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/offres"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              {t('common.seeAllOffers')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PackCroissancePage;
